import _ from "lodash";
import client from "lib/apolloClient/client";
import { QUESTION, QUESTION_V2 } from "./Constants";
import {
  getToddleLibraryGroupFeedQuery,
  getLibraryCommonFeedQuery,
  getLibraryCommonFeedQueryV2,
  getCollectionQuery,
  getLibraryConstantsQuery,
  getLibraryGradesQuery,
  getStaffGradesQuery,
  getOrganizationResourceQuery,
  getSubjectTopicsQuery,
  getAssessmentDetailsQuery,
  getCourseDetailsQuery,
  getCollectionGradesQuery,
  getAssessmentQuestionsForLibraryQuery,
  getMypPlannerElementSetsQuery,
  getMypCriterionQuery,
} from "./ToddleLibraryQuery";
import { entitySavedByNodeFragment } from "./ToddleLibraryFragments";
import { getOrganizationCurriculumProgramsQuery } from "modules/CommonQuery";
import { getIsToddleLibraryWorksheetQueriesV2Enabled } from "Worksheet/modules/WorksheetFeatureFlags";

const savedByFragmentMapping = {
  UNIT_PLAN: {
    typeName: "UnitPlan",
    fragment: "unitPlan",
    fragmentName: "unitSavedByNodeItem",
  },
  ASSESSMENT: {
    typeName: "Assessment",
    fragment: "assessment",
    fragmentName: "assessmentSavedByNodeItem",
  },
  LE_COLLECTION: {
    typeName: "LECollection",
    fragment: "leCollection",
    fragmentName: "leCollectionSavedByNodeItem",
  },
  QUESTION: {
    typeName: "OrganizationResource",
    fragment: "question",
    fragmentName: "questionSavedByNodeItem",
  },
  QUESTION_V2: {
    typeName: "Question",
    fragment: "questionV2",
    fragmentName: "questionV2SavedByNodeItem",
  },
  MOCK_EXAM: {
    typeName: "MockExam",
    fragment: "mockExam",
    fragmentName: "mockExamSavedByNodeItem",
  },
  WORKSHEET_ASSESSMENT: {
    typeName: "Assessment",
    fragment: "assessment",
    fragmentName: "assessmentSavedByNodeItem",
  },
};

export const getToddleLibraryGroupFeedQueryFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getToddleLibraryGroupFeedQuery,
      variables,
    });
    return queryData || {};
  } catch (e) {
    return {};
  }
};

export const getLibraryCommonFeedFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getLibraryCommonFeedQuery,
      variables,
    });
    return queryData || {};
  } catch (e) {
    return {};
  }
};

export const writeLibraryCommonFeedToCache = ({ variables, data }) => {
  try {
    client.writeQuery({
      query: getLibraryCommonFeedQuery,
      variables,
      data,
    });
  } catch (e) {}
};

export const getLibraryCommonFeedV2FromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getLibraryCommonFeedQueryV2,
      variables,
    });
    return queryData || {};
  } catch {
    return {};
  }
};

export const writeLibraryCommonFeedV2ToCache = ({ variables, data }) => {
  try {
    client.writeQuery({
      query: getLibraryCommonFeedQueryV2,
      variables,
      data,
    });
  } catch {
    return {};
  }
};

export const getCollectionFeedFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getCollectionQuery,
      variables,
    });
    return queryData || {};
  } catch (e) {
    return {};
  }
};

export const getCollectionGradesFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getCollectionGradesQuery,
      variables,
    });
    return queryData || {};
  } catch (e) {
    return {};
  }
};

export const getLibraryConstantsFromCache = () => {
  try {
    const queryData = client.readQuery({
      query: getLibraryConstantsQuery,
    });
    return queryData || {};
  } catch (e) {
    return {};
  }
};

export const getLibraryGradesFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getLibraryGradesQuery,
      variables,
    });
    return queryData || {};
  } catch (e) {
    return {};
  }
};

export const getStaffGradesFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getStaffGradesQuery,
      variables,
    });
    return queryData || {};
  } catch (e) {
    return {};
  }
};

export const writeSaveByNodeToCache = ({ entityType, entityId, data }) => {
  try {
    let normalizedEntityType = entityType;
    if (
      _.isEqual(entityType, QUESTION) &&
      getIsToddleLibraryWorksheetQueriesV2Enabled()
    ) {
      normalizedEntityType = QUESTION_V2;
    }

    const { typeName, fragment, fragmentName } = savedByFragmentMapping[
      normalizedEntityType
    ];

    client.writeFragment({
      id: `${typeName}:${entityId}`,
      fragment: entitySavedByNodeFragment[fragment],
      fragmentName,
      data: {
        ...data,
        ...(normalizedEntityType === QUESTION_V2 && {
          __typename: typeName,
        }),
      },
    });
  } catch (e) {}
};

export const getSaveByNodeFromCache = ({ entityType, entityId }) => {
  try {
    let normalizedEntityType = entityType;
    if (
      _.isEqual(entityType, QUESTION) &&
      getIsToddleLibraryWorksheetQueriesV2Enabled()
    ) {
      normalizedEntityType = QUESTION_V2;
    }

    const { typeName, fragment, fragmentName } = savedByFragmentMapping[
      normalizedEntityType
    ];

    const queryData = client.readFragment({
      id: `${typeName}:${entityId}`,
      fragment: entitySavedByNodeFragment[fragment],
      fragmentName,
    });

    return queryData || {};
  } catch (e) {
    return {};
  }
};

export const getOrganizationResourceFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getOrganizationResourceQuery,
      variables,
    });
    return _.get(queryData, "node") || {};
  } catch (e) {
    return {};
  }
};

export const getSubjectTopicsFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getSubjectTopicsQuery,
      variables,
    });
    return _.get(queryData, "multiNode") || {};
  } catch (e) {
    return {};
  }
};

export const getAssessmentDetailsFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getAssessmentDetailsQuery,
      variables,
    });
    return _.get(queryData, "node") || {};
  } catch (e) {
    return {};
  }
};

export const getCourseDetailsFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getCourseDetailsQuery,
      variables,
    });
    return _.get(queryData, "node") || {};
  } catch (e) {
    return {};
  }
};

export const getAssessmentQuestionsForLibraryFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getAssessmentQuestionsForLibraryQuery,
      variables,
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getMypPlannerElementSetsFromCache = params => {
  try {
    const queryData = client.readQuery({
      query: getMypPlannerElementSetsQuery,
      variables: params,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getMypCriterionFromCache = params => {
  try {
    const queryData = client.readQuery({
      query: getMypCriterionQuery,
      variables: params,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getOrganizationCurriculumProgramsFromCache = organizationId => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getOrganizationCurriculumProgramsQuery,
      variables: {
        organizationId,
      },
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};
